/**
 * An agent's usage against its limits: tokens used today as a bar with
 * "Today: 412K of 5M tokens · ≈ $0.06", when the limit resets in the
 * viewer's own time ("Resets at midnight UTC (7:00 PM your time)"), and the
 * last hour's turns beneath it. The text carries the numbers; the bar is a
 * glance aid only.
 *
 * The cost is an estimate (see `EstimatedCost`); a total that leaves out
 * calls with no known price shows a trailing "+". It is omitted entirely
 * when the BE doesn't send it.
 *
 * Shared by the Limits section of Agent settings and the runs dialog a
 * budget-exhausted notification opens, so both read from one query.
 */

import { useTranslation } from 'react-i18next'

import { EstimatedCost } from '@/components/EstimatedCost'
import { formatShortTimeInZone } from '@/i18n/formatters'
import { formatTokenCount } from '@/lib/format-cost'
import { useViewerTimeZone } from '@/lib/use-viewer-time-zone'

import { useAgentUsage } from './use-agent-runs'

interface AgentUsageMeterProps {
  agentId: string
}

export function AgentUsageMeter({ agentId }: AgentUsageMeterProps) {
  const { t, i18n } = useTranslation('agents')
  const usage = useAgentUsage(agentId)
  const timeZone = useViewerTimeZone()

  if (usage.isLoading) return <p className="text-xs text-fg-muted">{t('usage.loading')}</p>
  if (usage.isError || !usage.data) {
    return <p className="text-xs text-fg-muted">{t('usage.unavailable')}</p>
  }

  const {
    tokensToday,
    dailyTokenBudget,
    turnsLastHour,
    maxTurnsPerHour,
    resetsAt,
    costTodayUsd,
    costTodayComplete,
  } = usage.data
  const locale = i18n.language || 'en'
  const fraction = dailyTokenBudget > 0 ? Math.min(1, tokensToday / dailyTokenBudget) : 0
  const fill = fraction >= 1 ? 'bg-danger' : fraction >= 0.8 ? 'bg-warning' : 'bg-fg-muted'

  // The limit resets at midnight UTC. Say so, and add the viewer's clock
  // time unless their zone IS UTC (it would just repeat midnight).
  const localReset = formatShortTimeInZone(resetsAt, locale, timeZone)
  const utcReset = formatShortTimeInZone(resetsAt, locale, 'UTC')
  const resetLine =
    localReset && localReset !== utcReset
      ? t('usage.resets', { time: localReset })
      : t('usage.resetsUtc')

  return (
    <div className="flex flex-col gap-1.5">
      <div aria-hidden="true" className="h-1.5 w-full overflow-hidden rounded-full bg-bg-muted">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${fraction * 100}%` }} />
      </div>
      <p className="flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
        <span>
          {t('usage.tokens', {
            used: formatTokenCount(tokensToday, locale),
            budget: formatTokenCount(dailyTokenBudget, locale),
          })}
        </span>
        {costTodayUsd !== undefined && (
          <>
            <span aria-hidden="true">·</span>
            <EstimatedCost usd={costTodayUsd} partial={costTodayComplete === false} />
          </>
        )}
      </p>
      <p className="text-xs text-fg-muted">{resetLine}</p>
      <p className="text-xs text-fg-muted">
        {t('usage.turns', { used: turnsLastHour, max: maxTurnsPerHour })}
      </p>
    </div>
  )
}
